import React from 'react';
import { DollarSign, Calendar, Search, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useInView } from 'react-intersection-observer';

interface Sale {
  id: string;
  referral_code: string;
  sale_value: number;
  commission_value: number;
  sale_date: string;
  created_at: string;
  customer_name: string;
  user_name: string;
}

export function SalesList() {
  const [sales, setSales] = React.useState<Sale[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const { ref: loadMoreRef, inView } = useInView();

  const loadSales = React.useCallback(async (pageNumber: number, search: string = '') => {
    try {
      setIsFetchingMore(true);
      const from = pageNumber * 50;
      const to = from + 49;

      let query = supabase
        .from('sales')
        .select(`
          id,
          referral_code,
          sale_value,
          commission_value,
          sale_date,
          created_at,
          customer_name,
          referral_codes!inner (
            user_id
          )
        `, { count: 'exact' });

      if (search) {
        query = query.or(`referral_code.ilike.%${search}%,customer_name.ilike.%${search}%`);
      }

      const { data, error: fetchError, count } = await query
        .order('sale_date', { ascending: false })
        .range(from, to);

      if (fetchError) throw fetchError;

      // Transform the data to match our interface
      const formattedSales = await Promise.all(data?.map(async sale => {
        // Get user metadata
        const { data: userData } = await supabase.rpc('get_user_metadata', { 
          user_id: sale.referral_codes.user_id 
        });
        
        return {
          id: sale.id,
          referral_code: sale.referral_code,
          sale_value: sale.sale_value,
          commission_value: sale.commission_value,
          sale_date: sale.sale_date,
          created_at: sale.created_at,
          customer_name: sale.customer_name,
          user_name: userData?.name || 'Unknown'
        };
      }) || []);

      if (pageNumber === 0) {
        setSales(formattedSales);
      } else {
        setSales(prev => [...prev, ...formattedSales]);
      }

      setHasMore(count ? from + 50 < count : false);
    } catch (err) {
      console.error('Error loading sales:', err);
      setError('Erro ao carregar vendas');
    } finally {
      setIsFetchingMore(false);
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadSales(0, searchTerm);
    setPage(0);
  }, [loadSales, searchTerm]);

  React.useEffect(() => {
    if (inView && hasMore && !isFetchingMore && !isLoading) {
      setPage(prev => {
        const nextPage = prev + 1;
        loadSales(nextPage, searchTerm);
        return nextPage;
      });
    }
  }, [inView, hasMore, isFetchingMore, isLoading, loadSales, searchTerm]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  if (isLoading) {
    return <div className="text-center py-4">Carregando...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por código ou nome do cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="space-y-4">
        {sales.map((sale) => (
          <div
            key={sale.id}
            className="bg-gray-50 rounded-lg p-4 space-y-3"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <User size={20} className="text-gray-400" />
                <div>
                  <span className="font-medium text-gray-900">
                    {sale.customer_name}
                  </span>
                  <br />
                  <span className="text-sm text-gray-500">
                    Indicado por: {sale.user_name}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar size={16} />
                <span>{formatDate(sale.sale_date)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500">
                  Código: {sale.referral_code}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-gray-400" />
                <div>
                  <span className="text-sm text-gray-600">Valor da venda:</span>
                  <br />
                  <span className="font-medium text-gray-900">
                    {formatCurrency(sale.sale_value)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-green-500" />
                <div>
                  <span className="text-sm text-gray-600">Comissão:</span>
                  <br />
                  <span className="font-medium text-green-600">
                    {formatCurrency(sale.commission_value)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Infinite scroll trigger */}
        {hasMore && (
          <div ref={loadMoreRef} className="py-4 text-center">
            {isFetchingMore ? 'Carregando mais...' : ''}
          </div>
        )}

        {!isLoading && sales.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhuma venda encontrada
          </div>
        )}
      </div>
    </div>
  );
}